import { useMemo, useState } from 'react';
import type { AnalysisDisplayRules } from '@citizen-walk/shared-types';
import type { PostMinimal } from '../posts';

/**
 * 分析アプリ「スコア」タブ minimum (Sprint X5b / R-38 #8、旧 `AnalysisView.tsx` の scatter 部分を抽出移植)。
 *
 * minimum 仕様:
 *   - X = HappinessScore (-3 → +3)、Y = HealthScore (-3 → +3) の 4 象限 scatter
 *   - 旧 `getQuadrantColor` (HeatmapView L36-42) 踏襲: HH=ピンク / HU=紫 / UH=水色 / UU=青
 *   - dot click で onSelectPost → PostDetailModal 連動
 *   - 各象限件数 + 平均スコア summary
 *
 * 旧 AnalysisView との差分 (Sprint X5b narrow):
 *   - 軸 (xKey / yKey) は HappinessScore × HealthScore 固定 (M9 displayRules.scoreAxes 連動は X5c+)
 *   - chip 色 / filter / 凡例 toggle は scope 外
 *   - score を持たない post (extraFields に key 無し / 非 number) は集計対象外で「未評価」counter のみ
 *
 * 値ソース (docs/04 §1-1 PostDoc.extraFields):
 *   post.extraFields.HappinessScore / post.extraFields.HealthScore (number、-3〜+3 想定)
 *   旧 ECW は customFields 経由だが v2 は extraFields 一本 (docs/40 §7 統合方針踏襲)。
 */

export interface AnalysisScoreViewProps {
  posts: PostMinimal[];
  onSelectPost: (post: PostMinimal) => void;
  /** X 軸 / Y 軸 を切替可能にする予定。X5b は HappinessScore × HealthScore 固定。 */
  xKey?: string;
  yKey?: string;
  /**
   * Task D 動的化: M5 displayRules.score。指定されていれば xKey/yKey/xLabel/yLabel/quadrantColors
   * を rules から取得 (props xKey/yKey は更に高優先で fallback)。未指定なら従来 hardcode で動作。
   */
  scoreRules?: AnalysisDisplayRules['score'];
}

interface ScoredPost {
  post: PostMinimal;
  x: number;
  y: number;
}

/** 旧 HeatmapView L36-42 getQuadrantColor 踏襲 (default 色)。 */
const DEFAULT_QUADRANT_COLORS = {
  topRight: '#FF69B4', // 第 1 象限 Happy & Healthy (x>0, y>0)
  bottomRight: '#9C27B0', // 第 4 象限 Happy & Unhealthy (x>0, y<0)
  topLeft: '#00BCD4', // 第 2 象限 Unhappy & Healthy (x<0, y>0)
  bottomLeft: '#2196F3', // 第 3 象限 Unhappy & Unhealthy (x<0, y<0)
} as const;

function getQuadrantColor(
  x: number,
  y: number,
  override?: AnalysisDisplayRules['score'] extends infer R
    ? R extends { quadrantColors?: infer Q }
      ? Q
      : never
    : never,
): string {
  const colors = {
    topRight: override?.topRight ?? DEFAULT_QUADRANT_COLORS.topRight,
    bottomRight: override?.bottomRight ?? DEFAULT_QUADRANT_COLORS.bottomRight,
    topLeft: override?.topLeft ?? DEFAULT_QUADRANT_COLORS.topLeft,
    bottomLeft: override?.bottomLeft ?? DEFAULT_QUADRANT_COLORS.bottomLeft,
  };
  if (x > 0 && y > 0) return colors.topRight;
  if (x > 0 && y < 0) return colors.bottomRight;
  if (x < 0 && y > 0) return colors.topLeft;
  if (x < 0 && y < 0) return colors.bottomLeft;
  return '#9E9E9E'; // 軸上 (ゼロ含)
}

function readNumber(extraFields: Record<string, unknown> | null, key: string): number | null {
  if (!extraFields) return null;
  const v = extraFields[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

const PLOT_SIZE = 320; // svg width = height
const PLOT_MARGIN = 32;
const PLOT_INNER = PLOT_SIZE - PLOT_MARGIN * 2;
/** 値域 fallback (reviewer follow-up ⚠️-3 で M5 動的化、後方互換のための default)。 */
const DEFAULT_SCORE_MIN = -3;
const DEFAULT_SCORE_MAX = 3;

/** score → svg px (値域 [xMin,xMax] / [yMin,yMax] を closure に閉じ込めて返す)。 */
function makeAxisMappers(
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number,
): {
  xToPx: (x: number) => number;
  yToPx: (y: number) => number;
} {
  const xRange = xMax - xMin || 1; // 0 除算回避
  const yRange = yMax - yMin || 1;
  return {
    xToPx: (x) => PLOT_MARGIN + ((x - xMin) / xRange) * PLOT_INNER,
    // svg は上が小さい値 → y 反転
    yToPx: (y) => PLOT_MARGIN + ((yMax - y) / yRange) * PLOT_INNER,
  };
}

export function AnalysisScoreView({
  posts,
  onSelectPost,
  xKey: xKeyProp,
  yKey: yKeyProp,
  scoreRules,
}: AnalysisScoreViewProps) {
  const [hoverId, setHoverId] = useState<string | null>(null);

  // Task D 動的化: props > rules > hardcode default の優先順で軸 key/label を解決。
  const xKey = xKeyProp ?? scoreRules?.xKey ?? 'HappinessScore';
  const yKey = yKeyProp ?? scoreRules?.yKey ?? 'HealthScore';
  const xLabel = scoreRules?.xLabel ?? xKey;
  const yLabel = scoreRules?.yLabel ?? yKey;
  const quadrantColors = scoreRules?.quadrantColors;

  // reviewer follow-up ⚠️-3: 値域 (xMin/xMax/yMin/yMax) を rules から取得、未指定 fallback -3..+3。
  const xMin = scoreRules?.xMin ?? DEFAULT_SCORE_MIN;
  const xMax = scoreRules?.xMax ?? DEFAULT_SCORE_MAX;
  const yMin = scoreRules?.yMin ?? DEFAULT_SCORE_MIN;
  const yMax = scoreRules?.yMax ?? DEFAULT_SCORE_MAX;
  const { xToPx, yToPx } = useMemo(
    () => makeAxisMappers(xMin, xMax, yMin, yMax),
    [xMin, xMax, yMin, yMax],
  );

  // グリッド線の刻み: 値域に応じて整数 step を選ぶ (xMin..xMax の各単位、両端と 0 を除外)。
  const gridXTicks = useMemo(() => {
    const out: number[] = [];
    const start = Math.ceil(xMin);
    const end = Math.floor(xMax);
    for (let v = start; v <= end; v++) if (v !== xMin && v !== xMax && v !== 0) out.push(v);
    return out;
  }, [xMin, xMax]);
  const gridYTicks = useMemo(() => {
    const out: number[] = [];
    const start = Math.ceil(yMin);
    const end = Math.floor(yMax);
    for (let v = start; v <= end; v++) if (v !== yMin && v !== yMax && v !== 0) out.push(v);
    return out;
  }, [yMin, yMax]);

  const { scored, unscoredCount } = useMemo(() => {
    const out: ScoredPost[] = [];
    let unscored = 0;
    for (const p of posts) {
      const x = readNumber(p.extraFields, xKey);
      const y = readNumber(p.extraFields, yKey);
      if (x === null || y === null) {
        unscored++;
        continue;
      }
      out.push({ post: p, x, y });
    }
    return { scored: out, unscoredCount: unscored };
  }, [posts, xKey, yKey]);

  const quadrantStats = useMemo(() => {
    const tr = quadrantColors?.topRight ?? DEFAULT_QUADRANT_COLORS.topRight;
    const br = quadrantColors?.bottomRight ?? DEFAULT_QUADRANT_COLORS.bottomRight;
    const tl = quadrantColors?.topLeft ?? DEFAULT_QUADRANT_COLORS.topLeft;
    const bl = quadrantColors?.bottomLeft ?? DEFAULT_QUADRANT_COLORS.bottomLeft;
    const stats = {
      q1: { count: 0, label: `${xLabel}+ × ${yLabel}+`, color: tr },
      q2: { count: 0, label: `${xLabel}- × ${yLabel}+`, color: tl },
      q3: { count: 0, label: `${xLabel}- × ${yLabel}-`, color: bl },
      q4: { count: 0, label: `${xLabel}+ × ${yLabel}-`, color: br },
      axis: { count: 0, label: '軸上 / ゼロ', color: '#9E9E9E' },
    };
    for (const s of scored) {
      if (s.x > 0 && s.y > 0) stats.q1.count++;
      else if (s.x < 0 && s.y > 0) stats.q2.count++;
      else if (s.x < 0 && s.y < 0) stats.q3.count++;
      else if (s.x > 0 && s.y < 0) stats.q4.count++;
      else stats.axis.count++;
    }
    return stats;
  }, [scored, xLabel, yLabel, quadrantColors]);

  return (
    <div className="flex flex-col h-full overflow-auto bg-white">
      <div className="px-3 py-2 sm:px-4 sm:py-3 border-b border-slate-200 bg-slate-50 shrink-0">
        <h2 className="text-sm font-semibold text-slate-700">
          スコア分布 ({xLabel} × {yLabel})
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          評価済 {scored.length} 件 / 未評価 {unscoredCount} 件 (全 {posts.length} 件)
        </p>
      </div>

      <div className="flex flex-wrap gap-4 p-3 sm:gap-6 sm:p-4 items-start">
        {/* SVG quadrant scatter */}
        <div className="shrink-0">
          <svg
            width={PLOT_SIZE}
            height={PLOT_SIZE}
            viewBox={`0 0 ${PLOT_SIZE} ${PLOT_SIZE}`}
            role="img"
            aria-label={`${xLabel} 横軸 × ${yLabel} 縦軸の散布図、${scored.length} 件プロット`}
            className="border border-slate-200 rounded bg-white"
          >
            {/* 象限背景 (薄色) — Task D 動的化: quadrantColors を適用。 */}
            <rect
              x={xToPx(0)}
              y={yToPx(yMax)}
              width={xToPx(xMax) - xToPx(0)}
              height={yToPx(0) - yToPx(yMax)}
              fill={quadrantColors?.topRight ?? DEFAULT_QUADRANT_COLORS.topRight}
              fillOpacity={0.05}
            />
            <rect
              x={xToPx(xMin)}
              y={yToPx(yMax)}
              width={xToPx(0) - xToPx(xMin)}
              height={yToPx(0) - yToPx(yMax)}
              fill={quadrantColors?.topLeft ?? DEFAULT_QUADRANT_COLORS.topLeft}
              fillOpacity={0.05}
            />
            <rect
              x={xToPx(xMin)}
              y={yToPx(0)}
              width={xToPx(0) - xToPx(xMin)}
              height={yToPx(yMin) - yToPx(0)}
              fill={quadrantColors?.bottomLeft ?? DEFAULT_QUADRANT_COLORS.bottomLeft}
              fillOpacity={0.05}
            />
            <rect
              x={xToPx(0)}
              y={yToPx(0)}
              width={xToPx(xMax) - xToPx(0)}
              height={yToPx(yMin) - yToPx(0)}
              fill={quadrantColors?.bottomRight ?? DEFAULT_QUADRANT_COLORS.bottomRight}
              fillOpacity={0.05}
            />

            {/* グリッド線 (整数刻み、両端と 0 を除く)。値域は M5 score.xMin/xMax/yMin/yMax で可変。 */}
            {gridXTicks.map((v) => (
              <line
                key={`gridx-${v}`}
                x1={xToPx(v)}
                y1={PLOT_MARGIN}
                x2={xToPx(v)}
                y2={PLOT_SIZE - PLOT_MARGIN}
                stroke="#e2e8f0"
                strokeWidth={1}
              />
            ))}
            {gridYTicks.map((v) => (
              <line
                key={`gridy-${v}`}
                x1={PLOT_MARGIN}
                y1={yToPx(v)}
                x2={PLOT_SIZE - PLOT_MARGIN}
                y2={yToPx(v)}
                stroke="#e2e8f0"
                strokeWidth={1}
              />
            ))}

            {/* 軸 (太線) */}
            <line
              x1={xToPx(0)}
              y1={PLOT_MARGIN}
              x2={xToPx(0)}
              y2={PLOT_SIZE - PLOT_MARGIN}
              stroke="#64748b"
              strokeWidth={1.5}
            />
            <line
              x1={PLOT_MARGIN}
              y1={yToPx(0)}
              x2={PLOT_SIZE - PLOT_MARGIN}
              y2={yToPx(0)}
              stroke="#64748b"
              strokeWidth={1.5}
            />

            {/* 軸ラベル */}
            <text
              x={PLOT_SIZE - PLOT_MARGIN + 4}
              y={yToPx(0) - 4}
              fontSize={10}
              fill="#475569"
              textAnchor="end"
            >
              {xLabel} →
            </text>
            <text x={xToPx(0) + 4} y={PLOT_MARGIN - 4} fontSize={10} fill="#475569">
              ↑ {yLabel}
            </text>

            {/* dots */}
            {scored.map((s) => {
              const cx = xToPx(s.x);
              const cy = yToPx(s.y);
              const color = getQuadrantColor(s.x, s.y, quadrantColors);
              const isHover = hoverId === s.post.id;
              return (
                <circle
                  key={s.post.id}
                  cx={cx}
                  cy={cy}
                  r={isHover ? 8 : 6}
                  fill={color}
                  fillOpacity={0.8}
                  stroke="#fff"
                  strokeWidth={1.5}
                  className="cursor-pointer transition-all"
                  onClick={() => onSelectPost(s.post)}
                  onMouseEnter={() => setHoverId(s.post.id)}
                  onMouseLeave={() => setHoverId(null)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectPost(s.post);
                    }
                  }}
                  aria-label={`${s.post.title} (${xLabel}=${s.x}, ${yLabel}=${s.y})`}
                >
                  <title>
                    {s.post.title} ({xLabel}={s.x}, {yLabel}={s.y})
                  </title>
                </circle>
              );
            })}
          </svg>
        </div>

        {/* 凡例 + summary */}
        <div className="flex-1 min-w-[200px] space-y-3">
          <div>
            <h3 className="text-xs font-semibold text-slate-600 mb-2">象限別件数</h3>
            <ul className="space-y-1 text-xs">
              {(['q1', 'q4', 'q2', 'q3', 'axis'] as const).map((k) => (
                <li key={k} className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-sm"
                    style={{ backgroundColor: quadrantStats[k].color }}
                    aria-hidden
                  />
                  <span className="text-slate-700">{quadrantStats[k].label}</span>
                  <span className="ml-auto text-slate-500">{quadrantStats[k].count} 件</span>
                </li>
              ))}
            </ul>
          </div>
          {unscoredCount > 0 && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              {unscoredCount} 件は {xLabel} / {yLabel} が未入力のため非表示。 extraFields
              に数値を入れると scatter に表示されます。
            </p>
          )}
          <p className="text-[10px] text-slate-400">
            ※ Task D 動的化: M5 displayRules.score (xKey / yKey / xLabel / yLabel / quadrantColors)
            を反映。未指定なら HappinessScore × HealthScore で従来動作。
          </p>
        </div>
      </div>
    </div>
  );
}
